import { CheckIcon, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface OptionTile<T extends string> {
  value: T;
  icon: LucideIcon;
  title: string;
  hint: string;
  disabled?: boolean;
  /** Draws attention (e.g. quiet hours) without changing selection. */
  warn?: boolean;
}

/**
 * One-of-N choice as a row of tiles: an icon box (a check when chosen), a title and a one-line hint. Stacks when
 * the surrounding column is narrow, three across once it is 32rem or wider (a container query, so it adapts to the composer, not the viewport). Used for "who gets it" and "when" on Compose.
 */
export function OptionTiles<T extends string>({ value, onChange, options, ariaLabel, className }: { value: T; onChange: (v: T) => void; options: Array<OptionTile<T>>; ariaLabel: string; className?: string }) {
  return (
    <div className={cn('@container', className)}>
      <div role="radiogroup" aria-label={ariaLabel} className="grid gap-2 @lg:grid-cols-3">
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={o.disabled}
            onClick={() => onChange(o.value)}
            className={cn(
              'group flex items-start gap-3 border p-3 text-left transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50',
              on ? 'border-primary bg-accent/50' : 'hover:border-foreground/30 hover:bg-muted/40',
            )}
          >
            <span className={cn('grid size-9 shrink-0 place-items-center border transition-colors', on ? 'border-primary bg-primary text-primary-foreground' : o.warn ? 'bg-warning/15 text-warning' : 'bg-muted text-muted-foreground group-hover:text-foreground')}>
              {on ? <CheckIcon className="size-4" aria-hidden /> : <o.icon className="size-4" aria-hidden />}
            </span>
            <span className="grid min-w-0 gap-0.5">
              <span className="text-sm font-medium">{o.title}</span>
              <span className={cn('text-xs leading-snug', o.warn && !on ? 'text-warning' : 'text-muted-foreground')}>{o.hint}</span>
            </span>
          </button>
        );
      })}
      </div>
    </div>
  );
}
